import React, { useState, useEffect, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import './CodeTerminalSection.css';

// Standard-Inhalte (Fallback, falls keine Props übergeben werden)
const defaultCodeLines = [
  { text: 'const nexite = new Agency({', indent: 0 },
  { text: 'focus: "digitale Produkte",', indent: 1 },
  { text: 'region: "Köln / Bonn",', indent: 1 },
  { text: 'services: ["Web", "App", "UI/UX"],', indent: 1 },
  { text: '});', indent: 0 },
  { text: '', indent: 0 },
  { text: 'nexite.build(ihreIdee)', indent: 0 },
  { text: '.then(erfolg => launch(erfolg));', indent: 1 },
];

const CodeTerminalSection = ({
  terminalPosition = 'right', // 'right' | 'left'
  title = 'Von der Idee zum fertigen Produkt',
  highlight = 'fertigen Produkt', // Teil des Titels, der grün hervorgehoben wird
  description = 'Wir übersetzen Ihre Geschäftsziele in sauberen, wartbaren Code und durchdachtes Design. Strukturiert, transparent und auf messbaren Erfolg ausgerichtet – von der ersten Zeile bis zum Launch.',
  bullets = [
    'Moderne Web- & App-Architektur',
    'Sauberer, skalierbarer Code',
    'Design und Entwicklung aus einer Hand',
  ],
  ctaText = 'Projekt anfragen',
  ctaLink = '/projektanfrage',
  fileName = 'nexite.js',
  codeLines = defaultCodeLines,
}) => {
  const [displayed, setDisplayed] = useState([]);
  const [currentLine, setCurrentLine] = useState('');
  const [currentIndent, setCurrentIndent] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const containerRef = useRef(null);
  const startedRef = useRef(false);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !startedRef.current) {
          startedRef.current = true;
          typeLines();
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(node);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const typeLines = () => {
    const prefersReduced = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    if (prefersReduced) {
      setDisplayed(codeLines);
      setCurrentLine('');
      setIsTyping(false);
      return;
    }

    setIsTyping(true);
    let lineIndex = 0;
    let charIndex = 0;

    const tick = () => {
      if (lineIndex >= codeLines.length) {
        setIsTyping(false);
        setCurrentLine('');
        return;
      }

      const line = codeLines[lineIndex];
      setCurrentIndent(line.indent);

      if (charIndex <= line.text.length) {
        setCurrentLine(line.text.slice(0, charIndex));
        charIndex++;
        setTimeout(tick, 45);
      } else {
        setDisplayed((prev) => [...prev, line]);
        setCurrentLine('');
        lineIndex++;
        charIndex = 0;
        setTimeout(tick, 350);
      }
    };
    tick();
  };

  // Titel mit optionalem Highlight-Teil
  const renderTitle = () => {
    if (highlight && title.includes(highlight)) {
      const [before, after] = title.split(highlight);
      return (
        <>
          {before}
          <span>{highlight}</span>
          {after}
        </>
      );
    }
    return title;
  };

  return (
    <section
      className={`code-terminal-section ${
        terminalPosition === 'left' ? 'terminal-left' : ''
      }`}
      ref={containerRef}
    >
      <div className="code-terminal-container">
        {/* Titel – auf Mobile ganz oben */}
        <div className="code-terminal-heading">
          <h2 className="code-terminal-title">{renderTitle()}</h2>
        </div>

        {/* Restlicher Text */}
        <div className="code-terminal-text">
          {description && (
            <p className="code-terminal-desc">{description}</p>
          )}
          {bullets?.length > 0 && (
            <ul className="code-terminal-list">
              {bullets.map((b, i) => (
                <li key={i}>{b}</li>
              ))}
            </ul>
          )}
          {ctaText && (
            <NavLink to={ctaLink} className="code-terminal-cta">
              {ctaText}
            </NavLink>
          )}
        </div>

        {/* Terminal */}
        <div className="code-terminal-window" aria-hidden="true">
          <div className="code-terminal-bar">
            <span className="ct-dot ct-red"></span>
            <span className="ct-dot ct-yellow"></span>
            <span className="ct-dot ct-green"></span>
            <span className="code-terminal-filename">{fileName}</span>
          </div>
          <pre className="code-terminal-body">
            {displayed.map((line, i) => (
              <div
                key={i}
                className="ct-line"
                style={{ paddingLeft: `${line.indent * 1.5}rem` }}
              >
                {line.text || '\u00A0'}
              </div>
            ))}
            {isTyping && (
              <div
                className="ct-line"
                style={{ paddingLeft: `${currentIndent * 1.5}rem` }}
              >
                {currentLine}
                <span className="ct-cursor"></span>
              </div>
            )}
          </pre>
        </div>
      </div>
    </section>
    );
};

export default CodeTerminalSection;